import React, { useState } from "react";
import { X, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MagneticButton } from "../MagneticButton";
import { postJSON } from "../../lib/api";

/** Modal for creating a new Client. Only `name` is required.
 *  Optional fields live under an "Advanced details" accordion. */
export const NewClientModal = ({ onClose, onCreated }) => {
    const [form, setForm] = useState({
        name: "",
        industry: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        address: "",
        vat_number: "",
        nis2_in_scope: false,
    });
    const [advanced, setAdvanced] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const update = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const newErrors = {};
        if (!form.name.trim()) newErrors.name = "Required";
        if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
            newErrors.contact_email = "Invalid email";
        }
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        setSubmitting(true);
        try {
            const payload = Object.fromEntries(
                Object.entries(form).filter(([, v]) => v !== "" && v !== false || typeof v === "boolean"),
            );
            // Always include the nis2 flag (even when false, server defaults to false anyway)
            payload.nis2_in_scope = !!form.nis2_in_scope;
            const created = await postJSON("/ipam/clients", payload);
            onCreated(created);
        } catch (err) {
            if (err.status === 402) {
                const msg = err.payload?.detail?.message || "Client cap reached. Upgrade to MSP for unlimited.";
                toast.error(msg);
            } else if (err.status === 422 && err.payload?.detail) {
                toast.error("Check the form fields and try again.");
            } else {
                toast.error(err.message || "Create failed");
            }
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-canvas/80 backdrop-blur-md"
            data-testid="new-client-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-client-title"
        >
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 cursor-default"
                data-testid="new-client-backdrop"
            />
            <form
                onSubmit={submit}
                className="relative w-full max-w-lg rounded-2xl border border-white/[0.08] bg-surface/95 shadow-heavy-popover p-6 md:p-8"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-text-dim hover:text-text-primary p-1.5 rounded transition-colors"
                    aria-label="Close"
                    data-testid="new-client-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">New client</p>
                <h2 id="new-client-title" className="mt-2 font-display font-semibold text-2xl text-text-primary tracking-tight">
                    Create a client workspace
                </h2>
                <p className="mt-1 text-[12.5px] text-text-muted">
                    Only the name is required — you can fill the rest later.
                </p>

                <div className="mt-5 space-y-3">
                    <Field label="Client name *" error={errors.name}>
                        <input
                            autoFocus
                            type="text"
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            placeholder="Studio Rossi SRL"
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="new-client-name"
                        />
                    </Field>
                    <Field label="Industry">
                        <input
                            type="text"
                            value={form.industry}
                            onChange={(e) => update("industry", e.target.value)}
                            placeholder="Healthcare, Manufacturing, Legal, …"
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="new-client-industry"
                        />
                    </Field>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={form.nis2_in_scope}
                            onChange={(e) => update("nis2_in_scope", e.target.checked)}
                            className="accent-accent-cyan"
                            data-testid="new-client-nis2"
                        />
                        <span className="text-[12.5px] text-text-muted">
                            <span className="text-accent-mint">NIS2 in scope</span> (regulated entity)
                        </span>
                    </label>
                </div>

                <button
                    type="button"
                    onClick={() => setAdvanced((v) => !v)}
                    className="mt-5 inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent-cyan transition-colors"
                    data-testid="new-client-advanced-toggle"
                >
                    <ChevronDown className={`w-3 h-3 transition-transform ${advanced ? "rotate-180" : ""}`} />
                    Advanced details
                </button>

                {advanced && (
                    <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="new-client-advanced-fields">
                        <Field label="Contact name">
                            <input
                                type="text"
                                value={form.contact_name}
                                onChange={(e) => update("contact_name", e.target.value)}
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="new-client-contact-name"
                            />
                        </Field>
                        <Field label="Contact email" error={errors.contact_email}>
                            <input
                                type="email"
                                value={form.contact_email}
                                onChange={(e) => update("contact_email", e.target.value)}
                                placeholder="info@client.com"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="new-client-contact-email"
                            />
                        </Field>
                        <Field label="Contact phone">
                            <input
                                type="text"
                                value={form.contact_phone}
                                onChange={(e) => update("contact_phone", e.target.value)}
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="new-client-contact-phone"
                            />
                        </Field>
                        <Field label="VAT number">
                            <input
                                type="text"
                                value={form.vat_number}
                                onChange={(e) => update("vat_number", e.target.value)}
                                placeholder="IT01234567890"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="new-client-vat"
                            />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="Address">
                                <input
                                    type="text"
                                    value={form.address}
                                    onChange={(e) => update("address", e.target.value)}
                                    placeholder="Street, City, ZIP, Country"
                                    className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                    data-testid="new-client-address"
                                />
                            </Field>
                        </div>
                    </div>
                )}

                <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md border border-white/[0.08] text-[13px] text-text-muted hover:text-text-primary transition-colors"
                        data-testid="new-client-cancel"
                    >
                        Cancel
                    </button>
                    <MagneticButton
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan disabled:opacity-40"
                        ariaLabel="Create client"
                        data-testid="new-client-submit"
                    >
                        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        {submitting ? "Creating…" : "Create client"}
                    </MagneticButton>
                </div>
            </form>
        </div>
    );
};

const Field = ({ label, error, children }) => (
    <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">{label}</span>
        <div className="mt-1">{children}</div>
        {error && <span className="mt-1 block text-[11px] text-accent-red">{error}</span>}
    </label>
);

export default NewClientModal;
